import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LayoutDashboard } from 'lucide-react';

import useMediaQuery from '../../hooks/useMediaQuery';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';

import { CockpitStatusCard } from './cockpit';
import useScrollOverflow from './cockpit/useScrollOverflow';
import useWorkspaceScrollOwner from './cockpit/useWorkspaceScrollOwner';
import { adminRoutes } from './navigation/adminRoutes';
import { getRememberedSectionPath } from './navigation/adminPlaceMemory';
import { canAccessSection as checkSectionAccess, getAdminUser } from './navigation/sectionAccess';

// Viewports tall and wide enough to give the selected workspace a finite
// height with its own scroll region (when the chrome leaves it enough room;
// see useWorkspaceScrollOwner). Phones and short landscape keep the document
// as the scroll owner so the chrome scrolls away with the content.
export const FINITE_WORKSPACE_QUERY = '(min-width: 640px) and (min-height: 600px)';

// When a quick action navigates, focus continues on the destination's selected
// tab if the activated button disappears (it duplicated that tab) or the whole
// cockpit changed. Module scope survives the cockpit unmounting; the short
// window keeps a stale request from stealing focus later.
const ACTION_FOCUS_WINDOW_MS = 2000;
let pendingActionFocusAt = 0;

const normalizePath = (path) => (path || '').replace(/\/+$/, '') || '/';

const toStatusMetric = ({ id, title, value, subtitle, icon, tone }, index) => ({
  id: id ?? title ?? String(index),
  label: title,
  value,
  detail: subtitle,
  icon,
  tone,
});

/**
 * CockpitHome — the shared chrome for the 7 admin cockpit workspaces.
 *
 * Every cockpit route renders a selected section, so the chrome is budgeted
 * for task access (OWZ-71): one compact header row, exactly one status/KPI
 * band, then the page's own primary tabs and selected workspace (children).
 *   - header: cockpit icon + title, quick actions, and an Overview toggle
 *   - alerts: partial-error / critical warnings; always visible, never folded
 *   - KPI band: one CockpitStatusCard row; all metrics fit one row on desktop
 *     and adapt (never a forced five-column phone row) via container queries
 *   - overview: rich domain widgets behind one progressive disclosure, so they
 *     stay available without pushing the selected task below the fold
 *   - children: CockpitTabs + the selected tabpanel, adjacent to each other
 *
 * Props:
 *  - categoryId:       key into adminRoutes (e.g. 'students', 'finance')
 *  - description:      optional subtitle under the cockpit title
 *  - kpis:             array of { id, title, value, subtitle?, icon?, tone? }
 *                      where tone is 'good' | 'warn' | 'danger'
 *  - quickActions:     array of { label, icon?, path?, onClick?, sectionId? }.
 *                      Actions with a sectionId use the same access gate as
 *                      tabs; an action that only re-opens the current route
 *                      is hidden because the active tab already owns it.
 *  - alerts:           warnings rendered above the KPI band
 *  - overview:         full overview widgets (disclosed on demand)
 *  - overviewSummary:  optional short text shown on the Overview toggle
 *  - canAccessSection: optional (sectionId) => bool override; defaults to the
 *                      role-aware gate used across the admin panel
 *  - showSectionLinks: legacy grouped sub-section link list; the routed
 *                      cockpits pass false because CockpitTabs owns navigation
 *  - children:         CockpitTabs + the selected CockpitPanel. The panel is
 *                      the single scroll region when the workspace is finite
 *                      (data-scroll-owner="workspace"); otherwise the
 *                      document scrolls (data-scroll-owner="document").
 */
export const CockpitHome = ({
  categoryId,
  description,
  kpis = [],
  quickActions = [],
  alerts,
  overview,
  overviewSummary,
  canAccessSection,
  showSectionLinks = true,
  children,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const overviewId = useId();
  const pageRef = useRef(null);
  const actionsRef = useRef(null);
  const [overviewOpen, setOverviewOpen] = useState(false);
  const actionOverflow = useScrollOverflow(actionsRef);
  const workspaceEligible = useMediaQuery(FINITE_WORKSPACE_QUERY);
  const finiteWorkspace = useWorkspaceScrollOwner(pageRef, workspaceEligible);
  const cockpit = adminRoutes[categoryId];

  useEffect(() => {
    if (!pendingActionFocusAt || Date.now() - pendingActionFocusAt > ACTION_FOCUS_WINDOW_MS) return;
    pendingActionFocusAt = 0;
    const page = pageRef.current;
    if (!page || page.contains(document.activeElement)) return;
    page.querySelector('.admin-cockpit-tabs [role="tab"][aria-selected="true"]')?.focus();
  }, [location.pathname, location.search]);

  // Default to the same role-aware access gate the sidebar/dashboard use, so a
  // cockpit index route can be as simple as <CockpitHome categoryId="students" />.
  const adminUser = getAdminUser();
  const gate = canAccessSection ?? ((id) => checkSectionAccess(id, adminUser));
  const metrics = useMemo(() => kpis.map(toStatusMetric), [kpis]);

  if (!cockpit) {
    return null;
  }

  const CockpitIcon = cockpit.icon;
  const currentPath = normalizePath(location.pathname);
  const sections = cockpit.sections.filter((section) => gate(section.id));
  const accessibleQuickActions = quickActions.filter(
    (action) => (!action.sectionId || gate(action.sectionId))
      && !(action.path && normalizePath(action.path) === currentPath)
  );
  const hasActionStrip = accessibleQuickActions.length > 0 || Boolean(overview);

  const handleAction = (action) => {
    if (action.onClick) {
      action.onClick();
    } else if (action.path) {
      pendingActionFocusAt = Date.now();
      navigate(action.path);
    }
  };

  return (
    <div
      ref={pageRef}
      className="admin-cockpit-page"
      data-cockpit={categoryId}
      data-scroll-owner={finiteWorkspace ? 'workspace' : 'document'}
      data-overview-open={overview && overviewOpen ? 'true' : 'false'}
    >
      <header className="admin-cockpit-header">
        <div className="admin-cockpit-heading">
          <div className="admin-cockpit-heading-icon" aria-hidden="true">
            <CockpitIcon className="h-5 w-5 text-amber-100" />
          </div>
          <div className="min-w-0">
            <h1>{cockpit.label}</h1>
            {description && <p>{description}</p>}
          </div>
        </div>

        {hasActionStrip && (
          <div
            className="admin-cockpit-actions-frame"
            data-overflow-start={actionOverflow.start ? 'true' : 'false'}
            data-overflow-end={actionOverflow.end ? 'true' : 'false'}
          >
            <div
              ref={actionsRef}
              className="admin-cockpit-actions"
              role="group"
              aria-label={`${cockpit.label} actions`}
            >
              {overview && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="admin-cockpit-action"
                  aria-expanded={overviewOpen}
                  aria-controls={overviewOpen ? overviewId : undefined}
                  data-overview-toggle=""
                  onClick={() => setOverviewOpen((open) => !open)}
                >
                  <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                  <span className="admin-cockpit-action-label">Overview</span>
                  {overviewSummary && (
                    <span className="admin-cockpit-action-summary">{overviewSummary}</span>
                  )}
                  <ChevronDown
                    className={`h-4 w-4 transition-transform motion-reduce:transition-none ${overviewOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </Button>
              )}
              {accessibleQuickActions.map((action, index) => {
                const ActionIcon = action.icon;
                return (
                  <Button
                    key={action.id ?? action.label ?? index}
                    type="button"
                    variant="outline"
                    size="sm"
                    className="admin-cockpit-action"
                    title={action.label}
                    onClick={() => handleAction(action)}
                  >
                    {ActionIcon && <ActionIcon className="h-4 w-4" aria-hidden="true" />}
                    <span className="admin-cockpit-action-label">{action.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>
        )}
      </header>

      {alerts && <div className="admin-cockpit-alerts">{alerts}</div>}

      {metrics.length > 0 && (
        <CockpitStatusCard
          className="admin-cockpit-kpi-band"
          metrics={metrics}
          aria-label={`${cockpit.label} status`}
          data-cockpit-status-surface=""
        />
      )}

      {overview && overviewOpen && (
        <section
          id={overviewId}
          className="admin-cockpit-overview"
          aria-label={`${cockpit.label} overview`}
        >
          {overview}
        </section>
      )}

      {children}

      {/* Legacy grouped sub-section links (unused by the routed cockpits,
          which own navigation through CockpitTabs). */}
      {showSectionLinks && sections.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-stone-500 dark:text-stone-400 uppercase tracking-wide mb-3">
            Sections
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <Link
                  key={section.id}
                  to={getRememberedSectionPath(section, adminUser)}
                  className="group rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2"
                >
                  <Card className="admin-card h-full border border-stone-200 transition-all duration-300 group-hover:border-amber-500 group-hover:shadow-xl">
                    <CardContent className="flex items-center gap-3 p-4">
                      <div className="p-2 rounded-lg bg-stone-100 dark:bg-stone-800 group-hover:bg-amber-100 dark:group-hover:bg-amber-900/40 transition-colors">
                        <Icon className="h-5 w-5 text-stone-600 dark:text-stone-300" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-gray-800 dark:text-gray-100 truncate">
                          {section.label}
                        </p>
                        {section.description && (
                          <p className="text-xs text-stone-500 dark:text-stone-400 truncate">
                            {section.description}
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CockpitHome;
